<template lang="pug">
  management-page(title="Quick Responses"
                :creatable="false"
                :searchable="false"
                :showHeader="false")
    template(v-slot:body)
      .chart-configuration
        .time-range-filter
          aiq-date-picker(v-model="dateRange"
                          @change="handleDatePickerChange($event)"
                          type="daterange"
                          placeholder="Select date range"
                          popper-class="metrics-time-range__date-picker-popup"
                          :shortcuts="datePickerOptions.shortcuts"
                          :disabled-date="datePickerOptions.disabledDate"
                          :teleported="false"
                          format="YYYY/MM/DD"
                          :clearable="false"
                          :default-time="getDatePickerDefaultTime()")
      .quick-responses-table
        .quick-responses-table
          .table-toolbar
            aiq-button.export(@click="exportQuickResponses")
              i.iq-ico-export
            aiq-tooltip(placement="left" popper-class="info-tooltip")
              template(#content)
                .tooltip-content
                  | This table shows usage data for Quick Responses created in Settings > Admin > Quick Responses (listed under Teams in Conversations). It does not include Quick Responses created by individual agents (listed under Mine in Conversations). Only Quick Responses that have been used will appear in the table.
              .info-button-circle ?
        aiq-table(:data="tableData"
                  :default-sort="{prop: 'total_uses', order: 'descending'}"
                  v-loading="loading"
                  stripe)
          aiq-table-column(label="Quick Response" prop="quick_response_content" min-width="200" sortable show-overflow-tooltip)
            template(v-slot:default="scope")
              span {{ formatQuickResponse(scope.row.quick_response_content) }}
          aiq-table-column(label="Last Used Date" prop="last_used" width="180" sortable :formatter="formatDate")
          aiq-table-column(label="Type" prop="type" width="120" sortable label-class-name="is-bordered")
            template(v-slot:default="scope")
              .el-tag-container
                aiq-tag(v-if="scope.row.type" :type="primary") {{ formatType(scope.row.type) }}
          aiq-table-column(label="Total Uses" prop="total_uses" width="120" sortable align="center")
          aiq-table-column(label="Direct Uses" prop="direct_uses" width="120" sortable align="center")
          aiq-table-column(label="Edited Uses" prop="edited_uses" width="120" sortable align="center")
</template>

<script>
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import {datePickerDefaultOptions, defaultDateRange, lastAvailableMonthAndQuarterRange, createPickerDateRangeOption, dstDateRangeUtc } from '../../configs/dateRange';
import dayjs from '@/localPlugins/dayjs';
import { downloadAsFile, useTimeZone, getMetricsTimeZoneAbbr, formatDateWithTimezone } from '@/libs';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

export default {
  pageResource: '/metrics',
  name: 'QuickResponses',
  components: {
    ManagementPage,
  },
  computed: {
    datePickerOptions() {
      return {
        ...datePickerDefaultOptions(),
        shortcuts: lastAvailableMonthAndQuarterRange.map(createPickerDateRangeOption),
      };
    },
  },
  data() {
    return {
      dateRange: defaultDateRange(),
      tableData: [],
      loading: false,
      tzAbbr: '',
      timezone: '',
    };
  },
  mounted() {
    this.fetchQuickResponsesData();
  },
  methods: {
    handleDatePickerChange() {
      this.fetchQuickResponsesData();
    },
    async fetchQuickResponsesData() {
      this.loading = true;
      this.timezone = await useTimeZone(
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        this.$store.dispatch,
        this.$store._state.data.configs,
      );
      this.tzAbbr = getMetricsTimeZoneAbbr(this.timezone);
      try {
        const [periodStart, periodEnd] = dstDateRangeUtc(this.dateRange);
        const params = {
          period_start: periodStart,
          period_end: periodEnd,
          bucket: 'day',
        };
        const response = await this.$store.dispatch('metrics/getMetric', ['quick_response_usage', params]);
        const dataPoints = response?.dataset?.[0]?.data_points || [];
        this.tableData = dataPoints.map(item => ({
          quick_response_id: item.quick_response_id,
          quick_response_content: this.formatQuickResponse(item.quick_response_content),
          total_uses: item.total_uses,
          direct_uses: item.direct_uses,
          edited_uses: item.edited_uses,
          last_used: this.getLastUsedDate(item.uses),
          type: get(item, 'uses[0].usage_type'),
        }));
      } catch (error) {
        this.$message.error('Failed to load quick responses data');
      } finally {
        this.loading = false;
      }
    },
    getLastUsedDate(uses) {
      if (isEmpty(uses)) return null;
      return uses.reduce((latest, current) =>
        current.sent_at > latest.sent_at ? current : latest,
      ).sent_at;
    },
    formatDate(row) {
      return `${formatDateWithTimezone(row.last_used, this.timezone, 'MMM DD, YYYY hh:mm A')} ${this.tzAbbr}`;
    },
    formatQuickResponse(content) {
      // Remove surrounding quotes and capitalize first letter
      const cleaned = content.trim().replace(/^["']+|["']+$/g, '');
      return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    },
    exportQuickResponses() {
      if (!this.tableData || this.tableData.length === 0) {
        return;
      }

      const dataPoints = this.tableData.map(item => ({
        'Quick Response': this.formatQuickResponse(item.quick_response_content),
        'Type': this.formatType(item.type),
        'Last Used Date': item.last_used ? this.formatDate(item) : '',
        'Total Uses': item.total_uses || 0,
        'Direct Uses': item.direct_uses || 0,
        'Edited Uses': item.edited_uses || 0,
      }));

      const fileName = this.returnReportFileName('QuickResponses');

      downloadAsFile({
        dataType: 'table',
        data: dataPoints,
        filename: fileName,
      });
    },

    returnReportFileName(chartName = 'data') {
      const [startTimestamp, endTimestamp] = this.dateRange;
      const startDate = dayjs.isDayjs(startTimestamp)
        ? startTimestamp.format('YYYY-MM-DD')
        : dayjs(startTimestamp).format('YYYY-MM-DD');
      const endDate = dayjs.isDayjs(endTimestamp)
        ? endTimestamp.format('YYYY-MM-DD')
        : dayjs(endTimestamp).format('YYYY-MM-DD');
      return `${chartName}_${startDate}_${endDate}`;
    },
    formatType(type) {
      return type.toLowerCase() === 'all' ? 'All' : 'Team';
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.time-range-filter {
  position: relative;
  margin-left: auto;
}

.el-tag-container {
  display: flex;
  justify-content: left;
  align-items: center;
}

.chart-configuration {
  display: flex;
  margin-bottom: 24px;
}

.quick-responses-table {
  margin-top: 20px;

  .table-toolbar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;

    .el-button.export {
      height: 30px;
      width: auto;
      padding: 0 12px;
    }
    .info-button-circle {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      border-radius: 50%;
      background-color: #d3dce6;
      cursor: pointer;
      transition: background-color 0.2s;
      color: #ffffff;
      font-size: 13px;
      font-weight: bold;
      line-height: 2;
      text-align: center;
      &:hover {
        background-color: #909399;
      }
    }
  }
}
</style>